import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserIcon, 
  EnvelopeIcon, 
  KeyIcon, 
  ArrowRightOnRectangleIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { showSuccess, showError } from '../utils/notifications';
import API_CONFIG from '../config';

const Profile = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState({
    email: '',
    first_name: '',
    created_at: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    email: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }

      const userData = await response.json();
      setUserInfo({
        email: userData.email,
        first_name: userData.first_name || '',
        created_at: userData.created_at
      });

      setEditForm({
        first_name: userData.first_name || '',
        email: userData.email
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des informations:', error);
              showError('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      localStorage.removeItem('token');
      showSuccess('Déconnexion réussie');
      navigate('/login');
    }
  };

  const startEditing = () => {
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({
      first_name: userInfo.first_name,
      email: userInfo.email
    });
  };

  const saveChanges = async () => {
    if (!editForm.first_name.trim() || !editForm.email.trim()) {
      showError('Veuillez remplir tous les champs');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(editForm.email)) {
      showError('Veuillez entrer un email valide');
      return;
    }

    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/users/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: editForm.first_name.trim(),
          email: editForm.email.trim()
        })
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUserInfo({
          ...userInfo,
          first_name: updatedUser.first_name,
          email: updatedUser.email
        });
        
        setIsEditing(false);
        showSuccess('Profil mis à jour avec succès');
      } else {
        const errorData = await response.json();
        let errorMessage = 'Erreur lors de la mise à jour du profil';
        
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            const firstError = errorData.detail[0];
            if (firstError && firstError.msg) {
              errorMessage = firstError.msg;
            }
          }
        }
        
        showError(errorMessage);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      showError('Erreur de connexion lors de la mise à jour');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="max-w-md mx-auto bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du profil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white min-h-screen">
      {/* Header */}
      <div className="bg-primary text-white p-6">
        <div className="text-center">
          <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserIcon className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-xl font-bold">Mon Profil</h1>
        </div>
      </div>

      {/* Informations utilisateur */}
      <div className="p-6">
        <div className="space-y-4">
          {/* Prénom */}
          <div className="border-b border-gray-200 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <UserIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Prénom</p>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.first_name}
                      onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                      className="mt-1 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Votre prénom"
                    />
                  ) : (
                    <p className="font-medium text-gray-900">
                      {userInfo.first_name || 'Non renseigné'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Email */}
          <div className="border-b border-gray-200 pb-4">
            <div className="flex items-center space-x-3">
              <EnvelopeIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                {isEditing ? (
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    className="mt-1 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="votre@email.com"
                  />
                ) : (
                  <p className="font-medium text-gray-900">{userInfo.email}</p>
                )}
              </div>
            </div>
          </div>

          {/* Date d'inscription */}
          <div className="border-b border-gray-200 pb-4">
            <div className="flex items-center space-x-3">
              <KeyIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Membre depuis</p>
                <p className="font-medium text-gray-900">
                  {userInfo.created_at ? formatDate(userInfo.created_at) : 'Date non disponible'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="mt-8 space-y-3">
          {!isEditing ? (
            <button
              onClick={startEditing}
              className="w-full flex items-center justify-center space-x-2 bg-primary text-white py-3 px-4 rounded-lg hover:bg-primary-dark transition-colors"
            >
              <PencilIcon className="w-5 h-5" />
              <span>Modifier le profil</span>
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={saveChanges}
                className="flex-1 flex items-center justify-center space-x-2 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors"
              >
                <CheckIcon className="w-5 h-5" />
                <span>Sauvegarder</span>
              </button>
              <button
                onClick={cancelEditing}
                className="flex-1 flex items-center justify-center space-x-2 bg-gray-600 text-white py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
                <span>Annuler</span>
              </button>
            </div>
          )}

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            <span>Se déconnecter</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile; 